import { useEffect } from 'react';
import { LECTURA_DWELL_MS } from '@ahora-patagonia/contracts';
import { RelojDeLectura } from './relojDeLectura.js';
import { emitir, enviar } from './senales.js';

/** Cuánto del cuerpo hay que haber recorrido para considerar que se llegó al final. */
const FIN_DE_NOTA = 0.9;

/**
 * Todo lo que produce leer una nota: que se leyó, que se llegó al final, y cuánto tiempo llevó.
 *
 * **Este es el único camino.** Hasta acá la lectura viajaba por `POST /lecturas/:id` y el resto por
 * `/senales`; ahora `nota_leida` va en el mismo lote que las demás y el endpoint viejo quedó sin
 * usar. No cambia quién cuenta: el handler de esa señal llama al mismo `LecturasService.registrar`,
 * con su misma deduplicación, así que "Más leídas" del sitio sale de la misma cuenta que antes.
 *
 * Lo que se gana es que **la lectura y su tiempo viajan juntos**: eran dos pedidos por nota y ahora
 * es uno, y el tiempo ya no puede llegar sin su lectura.
 *
 * Las guardas son las mismas de siempre y por las mismas razones: se espera el dwell antes de
 * contar nada (el que abre y cierra no leyó), se descarta el prerender de Chrome y la pestaña
 * oculta, y el reloj solo corre con signos de vida (ver `RelojDeLectura`).
 *
 * El tiempo se manda **al irse**, que es el único momento en que se sabe.
 */
export function useSenalesDeNota(articleId: string | null | undefined): void {
  useEffect(() => {
    if (!articleId) return;
    if (typeof window === 'undefined') return;

    let reloj: RelojDeLectura | null = null;
    let termino = false;


    const empezar = () => {
      if (reloj || document.visibilityState !== 'visible') return;
      // La lectura se cuenta acá, cumplido el dwell: es el mismo momento en que la contaba el
      // aviso viejo, y la marca en `sessionStorage` evita que un F5 la vuelva a mandar.
      if (!yaContada(articleId)) {
        marcarContada(articleId);
        emitir({ tipo: 'nota_leida', articleId });
      }
      reloj = new RelojDeLectura(Date.now());
      window.addEventListener('scroll', alHaberVida, { passive: true });
      window.addEventListener('pointerdown', alHaberVida, { passive: true });
      window.addEventListener('keydown', alHaberVida);
      revisarFinal();
    };

    const alHaberVida = () => {
      reloj?.hayVida(Date.now());
      revisarFinal();
    };

    /**
     * ¿Llegó al final? Se mide contra el alto del documento y no contra el del artículo porque el
     * pie y las notas relacionadas van después: exigir el 100 % del documento sería exigir que el
     * lector pase de largo la nota entera.
     */
    const revisarFinal = () => {
      if (termino) return;
      const alto = document.documentElement.scrollHeight - window.innerHeight;
      if (alto <= 0) return;
      const recorrido = window.scrollY / alto;
      if (recorrido < FIN_DE_NOTA) return;
      termino = true;
      emitir({ tipo: 'nota_terminada', articleId });
    };

    const alCambiarVisibilidad = () => {
      if (document.visibilityState === 'visible') {
        if (reloj) reloj.reanudar(Date.now());
        else empezar();
      } else {
        reloj?.pausar(Date.now());
      }
    };

    // El dwell antes que nada: si la persona se va antes, no hubo lectura y no hay nada que medir.
    const arranque = window.setTimeout(empezar, LECTURA_DWELL_MS);
    document.addEventListener('visibilitychange', alCambiarVisibilidad);

    return () => {
      window.clearTimeout(arranque);
      document.removeEventListener('visibilitychange', alCambiarVisibilidad);
      window.removeEventListener('scroll', alHaberVida);
      window.removeEventListener('pointerdown', alHaberVida);
      window.removeEventListener('keydown', alHaberVida);

      // Al desmontar —navegó a otra nota o cerró— se reporta lo que se leyó. Menos de un segundo
      // no se manda: sería una señal por cada roce con la pantalla.
      const segundos = reloj?.segundos(Date.now()) ?? 0;
      if (segundos > 0) {
        emitir({ tipo: 'tiempo_en_nota', articleId, segundos });
        // Se fuerza el envío en vez de esperar la ventana: navegar dentro del sitio no dispara
        // `pagehide`, así que sin esto el tiempo de la nota anterior viajaría recién con el
        // siguiente lote, o no viajaría nunca si el lector cierra ahí.
        enviar();
      }
    };
  }, [articleId]);
}

const CLAVE = 'senales-nota-leida';

/**
 * Las notas ya contadas en esta pestaña. En `sessionStorage` y no en `localStorage`: la marca dura
 * lo que dura la visita, no para siempre — el que vuelve mañana a la misma nota es un lector nuevo.
 *
 * Es solo para ahorrar el pedido; quien manda de verdad es la deduplicación del servidor, que es la
 * única que un cliente no puede saltear. Todo va envuelto en `try` porque `sessionStorage` tira en
 * modo privado de algunos navegadores.
 */
function yaContada(articleId: string): boolean {
  try {
    return leerMarcas().includes(articleId);
  } catch {
    return false;
  }
}

function marcarContada(articleId: string): void {
  try {
    const marcas = [...leerMarcas().filter((id) => id !== articleId), articleId].slice(-50);
    window.sessionStorage.setItem(CLAVE, JSON.stringify(marcas));
  } catch {
    /* sin almacenamiento, manda el dedup del servidor */
  }
}

function leerMarcas(): string[] {
  const crudo = window.sessionStorage.getItem(CLAVE);
  if (!crudo) return [];
  const parsed: unknown = JSON.parse(crudo);
  return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
}
